import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, ChevronDown, ArrowUpRight } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from './ui/sheet';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { BTN_PRIMARY_GLOW, BTN_OUTLINE_GLOW } from '../lib/buttonStyles';

const LOGO_URL = 'https://customer-assets-4nw71qhi.emergentagent.net/job_48ccfe14-9a78-4511-9adb-e31d4f86bb3c/artifacts/qhjao7c6_ChatGPT%20Image%20Jul%2027%2C%202026%2C%2011_59_00%20PM.png';

const exploreLinks = [
  { to: '/dpdpa-act', label: 'DPDPA Act', desc: '9 chapters, 44 sections' },
  { to: '/dpdp-rules', label: 'DPDP Rules', desc: '23 rules, 7 schedules' },
  { to: '/compliance-guides', label: 'Compliance Guides', desc: 'Step-by-step playbooks' },
  { to: '/templates', label: 'Templates', desc: 'Notices, DPIAs, RoPA' },
  { to: '/consent-manager', label: 'Consent Manager', desc: 'How it works' },
  { to: '/blog', label: 'Privacy Blog', desc: 'Deep-dive articles' },
  { to: '/case-laws', label: 'Case Laws', desc: 'Precedent & context' },
  { to: '/news', label: 'DPDPA News', desc: 'Milestones & updates' },
  { to: '/resources', label: 'Resources', desc: 'Glossary & official links' },
];

const externalLinks = [
  { href: 'https://complyzen.in/academy', label: 'Academy' },
  { href: 'https://complyzen.in/products/zendpo', label: 'DPDPA Compliance Suite' },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setExploreOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-navy/85 backdrop-blur-2xl border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-3 group">
          <img src={LOGO_URL} alt="dpdpa.blog" className="w-9 h-9 group-hover:scale-105 transition-transform" />
          <span className="font-serif text-xl text-white tracking-tight">dpdpa<span className="text-saffron">.blog</span></span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 relative">
          <Link data-testid="nav-link-home" to="/" className="text-sm text-white/80 hover:text-cyan transition-colors">Home</Link>

          <div
            className="relative"
            onMouseEnter={() => setExploreOpen(true)}
            onMouseLeave={() => setExploreOpen(false)}
          >
            <button data-testid="nav-explore-trigger" className="flex items-center gap-1 text-sm text-white/80 hover:text-cyan transition-colors">
              Explore <ChevronDown size={14} className={`transition-transform ${exploreOpen ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {exploreOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-[560px] bg-navy/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl p-6 grid grid-cols-2 gap-2"
                  data-testid="nav-explore-menu"
                >
                  {exploreLinks.map((l) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      data-testid={`nav-explore-${l.to.replace('/', '')}`}
                      className="p-3 rounded-lg hover:bg-white/5 transition-colors group"
                    >
                      <p className="text-white text-sm font-medium group-hover:text-cyan transition-colors">{l.label}</p>
                      <p className="text-white/50 text-xs mt-0.5 font-mono">{l.desc}</p>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {externalLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-external-${l.label.replace(/\s/g, '-').toLowerCase()}`}
              className="text-sm text-white/80 hover:text-cyan transition-colors flex items-center gap-1"
            >
              {l.label} <ArrowUpRight size={12} />
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="https://complyzen.in/contact"
            data-testid="nav-book-consultation-button"
            className={BTN_OUTLINE_GLOW + ' !px-4 !py-2 text-sm'}
          >
            Book Consultation
          </a>
          <Link
            to="/compliance-assessment"
            data-testid="nav-compliance-assessment-button"
            className={BTN_PRIMARY_GLOW + ' !px-4 !py-2 text-sm'}
          >
            Compliance Assessment
          </Link>
        </div>

        <Sheet>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-trigger" className="lg:hidden text-white">
              <Menu size={26} />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-navy border-white/10 w-[85vw] overflow-y-auto">
            <VisuallyHidden.Root>
              <SheetTitle>Site navigation</SheetTitle>
            </VisuallyHidden.Root>
            <div className="flex flex-col gap-1 mt-10">
              <Link data-testid="mobile-nav-home" to="/" className="py-3 text-white border-b border-white/10">Home</Link>
              {exploreLinks.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`mobile-nav-${l.to.replace('/', '')}`} className="py-3 text-white/80 border-b border-white/10">{l.label}</Link>
              ))}
              {externalLinks.map((l) => (
                <a key={l.href} href={l.href} data-testid={`mobile-nav-${l.label.replace(/\s/g, '-').toLowerCase()}`} className="py-3 text-white/80 border-b border-white/10">{l.label} ↗</a>
              ))}
              <a href="https://complyzen.in/contact" data-testid="mobile-nav-book-consultation" className={BTN_OUTLINE_GLOW + ' mt-4 justify-center'}>Book Consultation</a>
              <Link to="/compliance-assessment" data-testid="mobile-nav-compliance-assessment" className={BTN_PRIMARY_GLOW + ' mt-2 justify-center'}>Compliance Assessment</Link>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
